import { useState, useEffect } from 'react';
import CookieConsentLib from 'react-cookie-consent';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { CookiePreferences } from '../../utils/cookieConsent';
import { getCookieConsent, getCookiePreferences } from '../../utils/cookieConsent';

const CookieConsent = () => {
  const { t } = useTranslation();
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(getCookiePreferences());
  const [hasConsent, setHasConsent] = useState<boolean>(getCookieConsent());
  
  // Listen for the custom event to open cookie settings
  useEffect(() => {
    const handleOpenSettings = () => {
      setShowSettings(true);
    };
    
    window.addEventListener('openCookieSettings', handleOpenSettings);
    
    return () => {
      window.removeEventListener('openCookieSettings', handleOpenSettings);
    };
  }, []);

  const handleAcceptAll = () => {
    const newPreferences = {
      essential: true,
      analytics: true,
      marketing: true,
    };
    
    setPreferences(newPreferences);
    setHasConsent(true);
    
    // Store the consent in localStorage
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(newPreferences));
    
    // Dispatch a custom event to notify other components that preferences have been updated
    const event = new CustomEvent('cookiePreferencesUpdated');
    window.dispatchEvent(event);
    
    // In the future, this is where you would store in Supabase
    // storeConsentInSupabase({
    //   essential: true,
    //   analytics: true,
    //   marketing: true,
    // });
  };

  const handleReject = () => {
    const newPreferences = {
      essential: true, // Essential cookies are always required
      analytics: false,
      marketing: false,
    };
    
    setPreferences(newPreferences);
    setHasConsent(true);
    
    // Store minimal consent
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(newPreferences));
    
    // Dispatch a custom event to notify other components that preferences have been updated
    const event = new CustomEvent('cookiePreferencesUpdated');
    window.dispatchEvent(event);
    
    // In the future, this is where you would store in Supabase
    // storeConsentInSupabase({
    //   essential: true,
    //   analytics: false,
    //   marketing: false,
    // });
  };

  const handleSavePreferences = () => {
    // Store the user's preferences
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    
    // Update the hasConsent state
    setHasConsent(true);
    
    // Dispatch a custom event to notify other components that preferences have been updated
    const event = new CustomEvent('cookiePreferencesUpdated');
    window.dispatchEvent(event);
    
    // In the future, this is where you would store in Supabase
    // storeConsentInSupabase(preferences);
    
    setShowSettings(false);
  };

  const handlePreferenceChange = (key: keyof CookiePreferences) => {
    if (key === 'essential') return; // Essential cookies cannot be toggled
    
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // If consent has already been given, don't show the banner
  if (hasConsent) {
    return (
      <>
        {/* Only show settings modal if opened */}
        {showSettings && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1001] p-4">
            <div className="bg-slate-800 p-6 rounded-lg max-w-md w-full">
              <h2 className="text-xl font-bold mb-4 text-white">{t('cookies.preferences')}</h2>
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-white">{t('cookies.essential')}</h3>
                    <p className="text-sm text-gray-300">{t('cookies.essentialDescription')}</p>
                  </div>
                  <div className="relative">
                    <input 
                      type="checkbox" 
                      checked={preferences.essential} 
                      disabled
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-600 rounded-full peer peer-checked:bg-blue-500"></div>
                    <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition peer-checked:translate-x-5"></div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-white">{t('cookies.analytics')}</h3>
                    <p className="text-sm text-gray-300">{t('cookies.analyticsDescription')}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={preferences.analytics} 
                      onChange={() => handlePreferenceChange('analytics')}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-600 rounded-full peer peer-checked:bg-blue-500"></div>
                    <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition peer-checked:translate-x-5"></div>
                  </label>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-white">{t('cookies.marketing')}</h3>
                    <p className="text-sm text-gray-300">{t('cookies.marketingDescription')}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={preferences.marketing} 
                      onChange={() => handlePreferenceChange('marketing')}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-600 rounded-full peer peer-checked:bg-blue-500"></div>
                    <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition peer-checked:translate-x-5"></div>
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowSettings(false)}
                  className="px-4 py-2 border border-gray-500 text-white rounded hover:bg-gray-700"
                >
                  {t('cookies.cancel')}
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  {t('cookies.save')}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <CookieConsentLib
        location="bottom"
        buttonText={t('cookies.accept')}
        declineButtonText={t('cookies.reject')}
        cookieName="cookieConsent"
        style={{ 
          background: "#1E293B", 
          color: "white",
          zIndex: 1000,
          padding: "1rem",
          borderRadius: "0.5rem",
          margin: "1rem",
          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)"
        }}
        buttonStyle={{ 
          background: "#3B82F6", 
          color: "white", 
          fontSize: "14px",
          borderRadius: "0.375rem",
          padding: "0.5rem 1rem"
        }}
        declineButtonStyle={{
          background: "transparent",
          border: "1px solid #CBD5E1",
          color: "white",
          fontSize: "14px",
          borderRadius: "0.375rem",
          padding: "0.5rem 1rem"
        }}
        enableDeclineButton
        onAccept={handleAcceptAll}
        onDecline={handleReject}
        expires={150} // Cookie expires after 150 days
      >
        <div className="flex flex-col space-y-4">
          <p className="text-sm md:text-base">
            {t('cookies.message')}
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                setShowSettings(true);
              }}
              className="text-sm text-blue-300 hover:text-blue-200 underline"
            >
              {t('cookies.customize')}
            </button>
            <Link to="/privacy" className="text-sm text-blue-300 hover:text-blue-200 underline">
              {t('cookies.learnMore')}
            </Link>
          </div>
        </div>
      </CookieConsentLib>

      {/* Cookie Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1001] p-4">
          <div className="bg-slate-800 p-6 rounded-lg max-w-md w-full">
            <h2 className="text-xl font-bold mb-4 text-white">{t('cookies.preferences')}</h2>
            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-white">{t('cookies.essential')}</h3>
                  <p className="text-sm text-gray-300">{t('cookies.essentialDescription')}</p>
                </div>
                <div className="relative">
                  <input 
                    type="checkbox" 
                    checked={preferences.essential} 
                    disabled
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 rounded-full peer peer-checked:bg-blue-500"></div>
                  <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition peer-checked:translate-x-5"></div>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-white">{t('cookies.analytics')}</h3>
                  <p className="text-sm text-gray-300">{t('cookies.analyticsDescription')}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={preferences.analytics} 
                    onChange={() => handlePreferenceChange('analytics')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 rounded-full peer peer-checked:bg-blue-500"></div>
                  <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition peer-checked:translate-x-5"></div>
                </label>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-white">{t('cookies.marketing')}</h3>
                  <p className="text-sm text-gray-300">{t('cookies.marketingDescription')}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={preferences.marketing} 
                    onChange={() => handlePreferenceChange('marketing')}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 rounded-full peer peer-checked:bg-blue-500"></div>
                  <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition peer-checked:translate-x-5"></div>
                </label>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowSettings(false)}
                className="px-4 py-2 border border-gray-500 text-white rounded hover:bg-gray-700"
              >
                {t('cookies.cancel')}
              </button>
              <button
                onClick={handleSavePreferences}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                {t('cookies.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;